import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Zap } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';

const schema = z
  .object({
    password: z
      .string()
      .min(8, 'Minimo 8 caratteri')
      .regex(/[A-Z]/, 'Serve almeno una lettera maiuscola')
      .regex(/[0-9]/, 'Serve almeno un numero'),
    confirmPassword: z.string(),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Le password non coincidono',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof schema>;

export default function UpdatePassword() {
  const navigate = useNavigate();
  const [checkingSession, setCheckingSession] = useState(true);
  const [canUpdate, setCanUpdate] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  useEffect(() => {
    let mounted = true;

    const check = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setCanUpdate(Boolean(data.session));
      } finally {
        if (mounted) setCheckingSession(false);
      }
    };

    void check();

    return () => {
      mounted = false;
    };
  }, []);

  const onSubmit = async ({ password }: FormData) => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success('Password aggiornata con successo');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Aggiornamento password fallito');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 flex items-center justify-center">
              <Zap size={18} className="text-white" />
            </div>
            <span className="text-xl font-bold gradient-text">BudgetAI</span>
          </Link>
          <h1 className="text-2xl font-bold text-white">Imposta nuova password</h1>
          <p className="text-slate-400 text-sm mt-1">Inserisci una nuova password per completare il recupero.</p>
        </div>

        <div className="card p-8 space-y-4">
          {checkingSession ? (
            <p className="text-sm text-slate-400">Verifica link in corso...</p>
          ) : null}

          {!checkingSession && !canUpdate ? (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              Link non valido o scaduto. Richiedi un nuovo link di recupero.
            </div>
          ) : null}

          {!checkingSession && canUpdate ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <Input
                type={showPassword ? 'text' : 'password'}
                label="Nuova password"
                placeholder="••••••••"
                autoComplete="new-password"
                leftIcon={<Lock size={16} />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="text-slate-400 hover:text-slate-200"
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
                error={errors.password?.message}
                {...register('password')}
              />

              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                label="Conferma password"
                placeholder="••••••••"
                autoComplete="new-password"
                leftIcon={<Lock size={16} />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    className="text-slate-400 hover:text-slate-200"
                    aria-label={showConfirmPassword ? 'Nascondi conferma password' : 'Mostra conferma password'}
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
                error={errors.confirmPassword?.message}
                {...register('confirmPassword')}
              />

              <Button type="submit" fullWidth size="lg" loading={isSubmitting}>
                Salva nuova password
              </Button>
            </form>
          ) : null}
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          Hai bisogno di un nuovo link?{' '}
          <Link to="/reset-password" className="text-brand-400 hover:text-brand-300 font-medium transition-colors">
            Recupera password
          </Link>
        </p>
      </div>
    </div>
  );
}
